import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Calculator as CalcIcon, Minus, Plus, Ship } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtNum, CURRENCIES, SIZE_LABELS } from "@/lib/constants";

export default function Calculator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [ports, setPorts] = useState([]);
  const [lines, setLines] = useState([]);
  const [filters, setFilters] = useState({
    shipment_type: "export", pol_id: "", pod_id: "", container_type: "dry",
    container_size: "", shipping_line_id: "all",
  });
  const [matches, setMatches] = useState([]);
  const [rateId, setRateId] = useState(params.get("rate_id") || "");
  const [qty, setQty] = useState(1);
  const [currency, setCurrency] = useState("USD");
  const [result, setResult] = useState(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    api.get("/ports").then(({ data }) => setPorts(data));
    api.get("/shipping-lines").then(({ data }) => setLines(data));
  }, []);

  // initial from rate_id
  useEffect(() => {
    if (params.get("rate_id")) {
      api.get(`/rates/${params.get("rate_id")}`).then(({ data }) => {
        setFilters((f) => ({
          ...f, shipment_type: data.shipment_type, pol_id: data.pol_id, pod_id: data.pod_id,
          container_type: data.container_type, container_size: data.container_size,
          shipping_line_id: data.shipping_line_id,
        }));
      });
    }
  }, [params]);

  // search matches when filters change (guarded against stale responses)
  useEffect(() => {
    const q = new URLSearchParams();
    q.set("shipment_type", filters.shipment_type);
    ["pol_id", "pod_id", "container_type", "container_size", "shipping_line_id"].forEach((k) => {
      if (filters[k]) q.set(k, filters[k]);
    });
    const seq = ++searchSeq.current;
    api.get(`/rates/search?${q.toString()}`).then(({ data }) => {
      if (seq !== searchSeq.current) return;
      setMatches(data.results);
      if (rateId && !data.results.find((r) => r.id === rateId)) setRateId("");
    });
  }, [filters]); // eslint-disable-line

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const calculate = () => {
    if (!rateId) return;
    api.post("/calculate", { rate_id: rateId, num_containers: qty, display_currency: currency })
      .then(({ data }) => setResult(data));
  };

  useEffect(() => { if (rateId) calculate(); }, [rateId, qty, currency]); // eslint-disable-line

  const drySizes = ["20GP", "40GP", "40HC"];
  const reeferSizes = ["20RF", "40RF", "40HCRF"];
  const sizes = filters.container_type === "reefer" ? reeferSizes : drySizes;

  return (
    <Layout>
      <div className="bg-navy text-white py-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-signal text-xs font-mono-data mb-2"><CalcIcon className="h-4 w-4" /> SHIPMENT COST CALCULATOR</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Estimate Your Total Shipment Cost</h1>
          <p className="text-white/60 mt-2">Ocean freight × containers + applicable local charges & taxes.</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-12 gap-8">
        {/* Inputs */}
        <div className="lg:col-span-8 space-y-5">
          <div className="border border-border rounded-md p-6 bg-card grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Shipment Type</Label>
              <Select value={filters.shipment_type} onValueChange={(v) => set("shipment_type", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-shipment-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="export">Export</SelectItem><SelectItem value="import">Import</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Shipping Line</Label>
              <Select value={filters.shipping_line_id} onValueChange={(v) => set("shipping_line_id", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-line"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Lines</SelectItem>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">POL</Label>
              <Select value={filters.pol_id} onValueChange={(v) => set("pol_id", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-pol"><SelectValue placeholder="Origin" /></SelectTrigger>
                <SelectContent>{ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">POD</Label>
              <Select value={filters.pod_id} onValueChange={(v) => set("pod_id", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-pod"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent>{ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Container Type</Label>
              <Select value={filters.container_type} onValueChange={(v) => { set("container_type", v); set("container_size", ""); }}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-ctype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="dry">Dry</SelectItem><SelectItem value="reefer">Reefer</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Container Size</Label>
              <Select value={filters.container_size} onValueChange={(v) => set("container_size", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="calc-size"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>{sizes.map((s) => <SelectItem key={s} value={s}>{SIZE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Rate picker */}
          <div className="border border-border rounded-md bg-card">
            <div className="px-5 py-3 border-b border-border font-semibold text-navy text-sm">Select a rate ({matches.length} available)</div>
            <div className="max-h-72 overflow-auto">
              {matches.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No matching rates. Adjust filters above.</div>}
              {matches.map((m) => (
                <button key={m.id} onClick={() => setRateId(m.id)} data-testid={`calc-rate-${m.id}`}
                  className={`w-full text-left px-5 py-3 border-b border-border last:border-0 flex justify-between items-center transition-colors ${rateId === m.id ? "bg-signal/10" : "hover:bg-secondary/60"}`}>
                  <div>
                    <div className="font-semibold text-navy text-sm">{m.shipping_line_name}</div>
                    <div className="text-xs font-mono-data text-muted-foreground">{m.pol_name} → {m.pod_name} · {m.size_label}</div>
                  </div>
                  <div className="font-mono-data font-semibold text-navy text-sm">{m.currency} {fmtNum(m.ocean_freight)}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="border border-border rounded-md p-6 bg-card flex flex-wrap items-center gap-6">
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Number of Containers</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-sm" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus"><Minus className="h-4 w-4" /></Button>
                <span className="font-mono-data font-bold text-2xl text-navy w-12 text-center" data-testid="qty-value">{qty}</span>
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-sm" onClick={() => setQty(qty + 1)} data-testid="qty-plus"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Display Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="rounded-sm h-11 w-32" data-testid="calc-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Receipt */}
        <div className="lg:col-span-4">
          <div className="sticky top-20 border border-border rounded-md bg-card overflow-hidden" data-testid="calc-receipt">
            <div className="bg-navy text-white p-5">
              <div className="text-xs uppercase text-white/50 tracking-wide">Estimated Shipment Cost</div>
              <div className="font-heading font-extrabold text-2xl mt-1">
                {result ? `${result.display_currency} ${fmtNum(result.grand_total_display)}` : "—"}
              </div>
            </div>
            {result ? (
              <div className="p-5 space-y-3 text-sm">
                <div className="font-mono-data text-xs text-muted-foreground">
                  {result.rate.shipping_line_name} · {result.rate.size_label}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ocean Freight ({qty}×)</span>
                  <span className="font-mono-data text-navy">{result.ocean_freight_currency} {fmtNum(result.ocean_freight_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Local Charges ({qty}×)</span>
                  <span className="font-mono-data text-navy">INR {fmtNum(result.local_charges_inr_total)}</span>
                </div>
                <div className="border-t border-border pt-2 text-xs text-muted-foreground">Converted to {result.display_currency}:</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Freight</span><span className="font-mono-data text-navy">{result.display_currency} {fmtNum(result.ocean_freight_display)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Local</span><span className="font-mono-data text-navy">{result.display_currency} {fmtNum(result.local_display)}</span></div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold">
                  <span className="text-navy">Grand Total</span>
                  <span className="font-mono-data text-signal">{result.display_currency} {fmtNum(result.grand_total_display)}</span>
                </div>
                <Button className="w-full bg-signal hover:bg-signal/90 text-white rounded-sm mt-2" onClick={() => navigate(`/enquiry?rate_id=${rateId}&qty=${qty}`)} data-testid="calc-plan-btn">
                  Plan This Shipment
                </Button>
                <p className="text-[11px] text-muted-foreground pt-1">Currency conversion is for estimation purposes only. Rates subject to final confirmation.</p>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <Ship className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
                Select a rate to see your estimated cost.
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
